import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, TextField, Autocomplete, MenuItem, ListItemText, Typography, LinearProgress } from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Dentist } from 'models/user';
import { getDentistsWithClinicTemplate } from 'apollo/templates/user/query.graphql';
import { debounce } from '@mui/material/utils';
import { Organization } from 'models/organization';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { getLabDefaultClinicName } from 'utils/lab-dentist-default-clinic';

type DentistsWithClinicSearchProps = {
    onSelect: (dentist: Dentist) => void;
    dentist: Dentist | null;
};
const DentistsWithClinicSearch: FC<DentistsWithClinicSearchProps> = ({ onSelect, dentist }) => {
    const { t: translate } = useTranslation(['users', 'client-directory', 'orders']);
    const [dentists, setDentists] = useState<Dentist[]>([]);
    const [selectedDentist, setSelectedDentist] = useState<Dentist>();
    const [inputValue, setInputValue] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();
    const lab = currentOrgVar();

    // The internal id this lab assigned to the dentist, shown under the dentist so duplicate
    // dentist profiles on the same clinic can be told apart while selecting one.
    const getLabLocalId = (dentist: Dentist) =>
        dentist.dentistLocalIds?.find((entry) => entry.lab === lab._id?.toString())?.localId;

    useEffect(() => {
        if (dentist && !isSelectedDentist(dentist)) {
            setDentists([dentist]);
            setSelectedDentist(dentist);
            setInputValue(dentist.fullNameWithClinicInfo || '');
        }
    }, [dentist]);

    const [getDentists, { loading }] = useLazyQuery(getDentistsWithClinicTemplate, {
        onError: (err) => {
            showBoundary(err);
        },
        onCompleted: ({ dentistsWithClinic: searchedDentists }) => {
            let newOptions: Dentist[] = [];
            if (selectedDentist) {
                newOptions = [selectedDentist];
            }
            if (searchedDentists) {
                newOptions = [...newOptions, ...searchedDentists];
            }
            setDentists(newOptions);
        },
    });

    const fetchDentists = useCallback(
        debounce((freeText: string) => {
            getDentists({
                variables: { filters: { freeText: freeText, active: true }, limit: 15 },
            });
        }, 500),
        [],
    );

    const isFormattedDentistClinic = (str: string) => str.split(' - ').length === 2;

    useEffect(() => {
        if (inputValue.length < 2) {
            setDentists([]);
            return undefined;
        }
        if (!isFormattedDentistClinic(inputValue)) {
            fetchDentists(inputValue);
        }
    }, [inputValue, fetchDentists]);

    const handleDentistChange = (dentist: Dentist) => {
        if (dentist.clinic && !isSelectedDentist(dentist)) {
            setSelectedDentist(() => dentist);
            onSelect(dentist);
        }
    };

    const isSelectedDentist = (dentist: Dentist) => {
        const dentistId = dentist ? dentist._id : null;
        const selectedDentistId = selectedDentist ? selectedDentist._id : null;
        if (!dentistId && !selectedDentistId) {
            return false;
        }
        return dentistId === selectedDentistId;
    };

    return (
        <Box>
            <Autocomplete
                key={selectedDentist?._id}
                fullWidth
                autoFocus
                autoComplete
                disableClearable
                options={dentists}
                getOptionLabel={(dentist) => dentist.fullNameWithClinicInfo || ''}
                filterOptions={(x) => x}
                includeInputInList
                filterSelectedOptions
                noOptionsText={translate('orders:TYPE_TO_SEARCH')}
                renderOption={(props, dentist) => {
                    const matches = match(dentist.fullNameWithClinicInfo || '', inputValue);
                    const parts = parse(dentist.fullNameWithClinicInfo || '', matches);
                    const clinic = dentist.clinic as Organization;
                    const localId = getLabLocalId(dentist);
                    const defaultClinicName = getLabDefaultClinicName(dentist, clinic?._id);

                    return (
                        <MenuItem {...props} key={`${dentist._id}-${clinic?._id}`} value={dentist._id}>
                            <ListItemText>
                                {parts.map((part, index) => {
                                    return (
                                        <Box
                                            key={index}
                                            component="span"
                                            sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                                        >
                                            {part.text}
                                        </Box>
                                    );
                                })}
                                {localId ? (
                                    <Box>
                                        <Typography variant="caption" color="black.700">
                                            {translate('users:LOCAL_DENTIST_ID')}:{' '}
                                            {parse(localId, match(localId, inputValue)).map((part, index) => (
                                                <Box
                                                    key={index}
                                                    component="span"
                                                    sx={{
                                                        fontWeight: part.highlight ? 'bold' : 'regular',
                                                    }}
                                                >
                                                    {part.text}
                                                </Box>
                                            ))}
                                        </Typography>
                                    </Box>
                                ) : undefined}
                                {defaultClinicName ? (
                                    <Box>
                                        <Typography variant="caption" color="black.700">
                                            {translate('users:DEFAULT_CLINIC')}: {defaultClinicName}
                                        </Typography>
                                    </Box>
                                ) : undefined}
                                <Box>
                                    <Typography variant="caption" color="grey.700">
                                        {translate(`client-directory:${dentist.dentistType}`)}
                                        {dentist.email ? `, ${dentist.email}` : ''}
                                    </Typography>
                                </Box>
                            </ListItemText>
                        </MenuItem>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        required
                        label={translate('users:SELECT_DENTIST')}
                        data-test="dentists-with-clinic.search-input"
                    />
                )}
                inputValue={inputValue}
                value={selectedDentist}
                onChange={(_, dentist) => {
                    handleDentistChange(dentist as Dentist);
                }}
                onInputChange={(_, value) => {
                    setInputValue(value);
                }}
                loading={loading}
                autoHighlight
            />
            {loading ? <LinearProgress /> : undefined}
        </Box>
    );
};

export default DentistsWithClinicSearch;
